"use client";

import { Check, CircleAlert, RefreshCw, WifiOff } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { useOnlineStatus } from "@/app/hooks/useOnlineStatus";
import { useSession } from "@/app/hooks/useSession";
import { Button } from "@/components/ui/button";
import type { SyncState } from "@/lib/types";

interface SyncStatusProps {
  syncState: SyncState;
  isSyncing: boolean;
  onManualSync: () => Promise<void> | void;
}

export function SyncStatus({ syncState, isSyncing, onManualSync }: SyncStatusProps) {
  const t = useTranslations("sync");
  const [showBadge, setShowBadge] = useState(false);
  const [badgeType, setBadgeType] = useState<"success" | "error" | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const { isOffline } = useOnlineStatus();
  const { isAuthenticated } = useSession();

  // Handle badge display with auto-fade
  useEffect(() => {
    if (syncState.status === "success" || syncState.status === "error") {
      // Reset animation state before showing badge (critical for re-triggering animation)
      setIsMounted(false);
      setBadgeType(syncState.status);
      setShowBadge(true);

      // Trigger fade-in animation
      requestAnimationFrame(() => {
        setIsMounted(true);
      });

      // Only auto-hide success badges after 2 seconds
      // Error badges stay visible until next sync
      if (syncState.status === "success") {
        const timer = setTimeout(() => {
          setIsMounted(false);
          // Wait for fade-out animation before hiding
          setTimeout(() => {
            setShowBadge(false);
          }, 300);
        }, 2000);

        return () => clearTimeout(timer);
      }
    }
  }, [syncState.status, syncState.lastSynced]);

  // Don't show sync status if user is not authenticated
  if (!isAuthenticated) {
    return null;
  }

  if (isOffline) {
    return (
      <Button
        variant="mute"
        size="auto"
        disabled={true}
        title={t("offline")}
        aria-label={t("offline")}
      >
        <WifiOff className={`size-6`} strokeWidth={1.5} />
      </Button>
    );
  }

  return (
    <Button
      variant="mute"
      size="auto"
      onClick={() => {
        void onManualSync();
      }}
      disabled={isSyncing}
      title={t("syncNow")}
      aria-label={t("syncNow")}
      className="relative"
    >
      <RefreshCw
        className={`size-6 transition-transform ${isSyncing ? "animate-spin" : ""}`}
        strokeWidth={1.5}
      />

      {/* Success/Error Badge */}
      {showBadge && badgeType && !isSyncing && (
        <span
          className={`absolute -top-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full transition-all duration-300 ease-out ${
            isMounted ? "opacity-100 scale-100" : "opacity-0 scale-50"
          } ${badgeType === "success" ? "bg-green-700 dark:bg-green-600" : "bg-destructive"}`}
        >
          {badgeType === "success" ? (
            <Check className="size-2.5 text-white" strokeWidth={4} />
          ) : (
            <CircleAlert className="size-2.5 text-white" strokeWidth={4} />
          )}
        </span>
      )}
    </Button>
  );
}
